import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDocDashboard } from "@/lib/erp/docs";
import type { DocDashboardResponse, DashboardSection } from "@/lib/erp/docs";
import { getQueryOptions } from "@/lib/config/queryConfig";
import { stock } from "@/lib/erp/stock";
import type { Bin } from "@/lib/erp/types/stock";

export interface StockTotals {
  actual: number;
  reserved: number;
  incoming: number; // Incoming stock (ERPNext semantics: Bin.ordered_qty)
  available: number; // Actual - Reserved
}

export interface StockInsights {
  warehouseCount: number;
  utilizationPercentage: number; // Percentage of actual stock that's reserved
  healthStatus: 'healthy' | 'low' | 'critical'; // Based on available vs safety stock
}

/**
 * Hook for fetching stock levels for an item across all warehouses
 *
 * Uses the same source as the Dashboard tab (zaviago_backend.api.dashboard.get_doc_dashboard),
 * so "Incoming" always matches the dashboard/ERPNext semantics.
 *
 * @param itemCode - Item code (must be provided for query to run)
 * @param enabled - Whether to fetch (should be true only for stock items)
 * @returns React Query result with stock totals (always present, defaults to zeros if no data)
 */
export function useItemStock(itemCode: string | undefined, enabled: boolean = true) {
  const query = useQuery<DocDashboardResponse>({
    // IMPORTANT: share cache with DocDashboardTab
    queryKey: ["doc-dashboard", "Item", itemCode],
    queryFn: () => {
      if (!itemCode) throw new Error("itemCode is required");
      return getDocDashboard("Item", itemCode);
    },
    enabled: !!itemCode && enabled && itemCode !== "new",
    ...getQueryOptions("detail"),
  });

  const stockSummary = useMemo(() => {
    const sections = query.data?.sections ?? [];
    return (
      sections.find(
        (s: DashboardSection) =>
          s.id === "stock_summary" || (s.type === "chart" && s.meta?.chart_type === "stock_balance")
      ) ?? null
    );
  }, [query.data?.sections]);

  const totals = useMemo<StockTotals>(() => {
    const series = stockSummary?.series ?? [];
    const actual = Number(series.find(s => s.id === "actual_qty")?.data?.[0]?.y ?? 0);
    const reserved = Number(series.find(s => s.id === "reserved_qty")?.data?.[0]?.y ?? 0);
    const incoming = Number(series.find(s => s.id === "incoming_qty")?.data?.[0]?.y ?? 0);

    if (!actual && !reserved && !incoming) {
      return {
        actual: 0,
        reserved: 0,
        incoming: 0,
        available: 0,
      };
    }

    const reservedNormalized = Math.max(0, Math.min(reserved, actual));
    const available = Math.max(0, actual - reservedNormalized);

    return {
      actual,
      reserved: reservedNormalized,
      incoming,
      available,
    };
  }, [stockSummary?.series]);

  const insights = useMemo<StockInsights>(() => {
    if (!query.data?.sections?.length) {
      return {
        warehouseCount: 0,
        utilizationPercentage: 0,
        healthStatus: 'healthy',
      };
    }

    const warehouseCount = Number((stockSummary?.meta as any)?.warehouse_count ?? 0);

    // Utilization: percentage of actual stock that's reserved
    const utilizationPercentage = totals.actual > 0
      ? Math.min(100, Math.round((totals.reserved / totals.actual) * 100))
      : 0;

    // Health status: compare available stock to a reasonable threshold
    // Critical: available < 10% of actual (or zero if no stock)
    // Low: available < 30% of actual
    // Healthy: otherwise
    let healthStatus: 'healthy' | 'low' | 'critical' = 'healthy';
    if (totals.actual > 0) {
      const availablePercentage = (totals.available / totals.actual) * 100;
      if (totals.available === 0 || availablePercentage < 10) {
        healthStatus = 'critical';
      } else if (availablePercentage < 30) {
        healthStatus = 'low';
      }
    } else if (totals.available === 0 && totals.incoming === 0) {
      healthStatus = 'critical';
    }

    return {
      warehouseCount,
      utilizationPercentage,
      healthStatus,
    };
  }, [query.data?.sections, stockSummary?.meta, totals]);

  return {
    ...query,
    totals,
    insights,
  };
}

/**
 * Hook for fetching per-warehouse Bin records for an item.
 * Used by the inline detail panel StockTab to show warehouse-level breakdown.
 * Uses stock.perItem() which calls frappeList (respects tenant permissions).
 */
export function useItemWarehouseStock(itemCode: string, enabled: boolean) {
  return useQuery<Bin[]>({
    queryKey: ["stock", "bin", itemCode],
    enabled: enabled && !!itemCode,
    queryFn: () => stock.perItem(itemCode),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}






